import type { InferOutput } from 'valibot'
import { SolrParamsKey } from '#shared/solr'
import { array, intersect, nullish, object, string } from 'valibot'
import { baseBodySchema } from '~/utils/solr/body'
import { headersSchema } from '~/utils/solr/headers'
import { solrHighlightingSchema } from '~/utils/solr/highlighting'
import { lawAndCommentarySchema } from '~/utils/solr/law-and-commentary'
import { searchLawAndLawCommentaryParamsSchema } from '~/utils/solr/params'

export const solrLawCommentaryResponseSchema = object({
  responseHeader: headersSchema,
  response: intersect([
    baseBodySchema,
    object({
      docs: array(lawAndCommentarySchema),
    }),
  ]),
  highlighting: nullish(solrHighlightingSchema),
})

export type SolrLawCommentaryResponseSchema = InferOutput<typeof solrLawCommentaryResponseSchema>

export const solrLawCommentaryParamsSchema = object({
  [SolrParamsKey.LawId]: nullish(string()),
  [SolrParamsKey.CommentIdsByLawId]: nullish(string()),
  [SolrParamsKey.CommentByEzMainNodeId]: nullish(string()),
  [SolrParamsKey.LiteratureListByLawId]: nullish(string()),
  [SolrParamsKey.InfoNoticeByEzObjectId]: nullish(string()),
  [SolrParamsKey.SearchLawAndLawCommentary]: nullish(searchLawAndLawCommentaryParamsSchema),
  [SolrParamsKey.SearchFullText]: nullish(string()),
  [SolrParamsKey.LawReferenceId]: nullish(string()),
})

export type SolrLawCommentaryParams = InferOutput<typeof solrLawCommentaryParamsSchema>

export function useLawCommentaryQuery(endpoint: 'search' | 'law-commentary', params: MaybeRef<SolrLawCommentaryParams>) {
  const hasLicense = useHasLicense()

  const options = useSolrQueryOptions({
    endpoint,
    params,
    schema: solrLawCommentaryResponseSchema,
    enabled: (currentParams) => {
      const hasValidValue = Object.values(currentParams ?? {}).some((value) => {
        return typeof value === 'string' ? value.trim() !== '' : value != null
      })

      const protectedKeys: readonly (keyof SolrLawCommentaryParams)[] = [
        SolrParamsKey.CommentByEzMainNodeId,
        SolrParamsKey.LiteratureListByLawId,
        SolrParamsKey.InfoNoticeByEzObjectId,
      ] as const

      const isProtected = protectedKeys.some(key => Boolean(currentParams?.[key]))

      return isProtected ? (hasValidValue && hasLicense.value) : hasValidValue
    },
  })

  return useQuery(options)
}
